import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, FileText, Image, Clock, User, Eye, BoxIcon ,Table2 ,Download, Plus, Box, FileDown } from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import FileUploadArea from '../FileUploadArea';
import fileProcessingService from '../../services/FileProcessingService';
import ContextMenu from '../ContextMenu';
import exportService from '../../services/exportService';
import { useWebSocket } from '../../contexts/WebSocketContext';

export function FilesView({
  project,
  collection,
  userInfo,
  onBack,
  onFileClick,
  searchQuery,
  sortBy,
  viewMode,
  onFileDeleted,  // New callback to notify parent when a file is deleted
  onCollectionUpdated,  // Callback to update cache when files are uploaded
  shouldRefreshData  // Flag to trigger data refresh when coming back from Workspace
}) {
  const { joinProject, leaveProject, subscribe } = useWebSocket();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadArea, setShowUploadArea] = useState(false);
  const fileInputRef = useRef(null);
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    selectedFile: null
  });
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Join WebSocket room when component mounts
  useEffect(() => {
    if (project?._id && userInfo?.userId) {
      // Join the project room to receive file events
      joinProject(project._id);
      console.log(`🔌 FilesView: Joined project room: ${project._id}`);

      // Clean up: leave room when component unmounts
      return () => {
        leaveProject(project._id);
        console.log(`🔌 FilesView: Left project room: ${project._id}`);
      };
    }
  }, [project, userInfo, joinProject, leaveProject]);

  // Subscribe to WebSocket file events
  useEffect(() => {
    if (!project?._id) return;

    // For root files view, collection will be null
    const isRootView = !collection;
    const currentCollectionId = collection?._id || null;

    // Subscribe to file events
    const unsubscribeUploaded = subscribe('file:uploaded', (data) => {
      console.log('📥 FilesView received file:uploaded:', data);
      // Handle events for this collection (or root if both are null)
      if (data.collectionId === currentCollectionId) {
        // Fetch fresh data to get the new file
        fetchFiles();
      }
    });

    const unsubscribeProcessing = subscribe('file:processing', (data) => {
      console.log('📥 FilesView received file:processing:', data);
      if (data.collectionId === currentCollectionId) {
        // Update file status to processing
        setFiles(prev => prev.map(file =>
          file._id === data.fileId
            ? { ...file, processingStatus: data.status }
            : file
        ));
      }
    });

    const unsubscribeCompleted = subscribe('file:completed', (data) => {
      console.log('📥 FilesView received file:completed:', data);
      if (data.collectionId === currentCollectionId) {
        // Update file with completed status and table count
        setFiles(prev => prev.map(file =>
          file._id === data.file.id
            ? { ...file,
                processingStatus: 'completed',
                tableCount: data.file.tableCount
              }
            : file
        ));
      }
    });

    const unsubscribeFailed = subscribe('file:failed', (data) => {
      console.log('📥 FilesView received file:failed:', data);
      if (data.collectionId === currentCollectionId) {
        // Update file status to failed
        setFiles(prev => prev.map(file =>
          file._id === data.fileId
            ? { ...file, processingStatus: 'failed' }
            : file
        ));
      }
    });

    const unsubscribeDeleted = subscribe('file:deleted', (data) => {
      console.log('📥 FilesView received file:deleted:', data);
      if (data.collectionId === currentCollectionId) {
        // Remove deleted file from list
        setFiles(prev => prev.filter(file => file._id !== data.fileId));
      }
    });

    // Clean up subscriptions
    return () => {
      unsubscribeUploaded();
      unsubscribeProcessing();
      unsubscribeCompleted();
      unsubscribeFailed();
      unsubscribeDeleted();
    };
  }, [project, collection, subscribe]);

  useEffect(() => {
    if (collection && userInfo?.userId) {
      fetchFiles();
    }
  }, [collection, userInfo]);

  // Refresh data when coming back from Workspace with changes
  useEffect(() => {
    if (shouldRefreshData && collection && userInfo?.userId) {
      fetchFiles();
    }
  }, [shouldRefreshData]);

  const fetchFiles = useCallback(async () => {
    if (!collection?._id || !userInfo?.userId) return;

    try {
      setLoading(true);
      setError(null);
      const collectionData = await ProjectService.getCollectionDetails(collection._id, userInfo.userId);
      setFiles(collectionData.files || []);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(err.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [collection, userInfo]);

  const handleFileUpload = async (uploadedFiles) => {
    if (!uploadedFiles.length || !collection?._id || !userInfo?.userId) return;

    try {
      // Determine project type based on first file
      const firstFile = uploadedFiles[0];
      const projectType = firstFile.type === 'application/pdf' ? 'pdf' : 'snippet';

      console.log('📤 Starting file upload for collection:', {
        collectionId: collection._id,
        fileCount: uploadedFiles.length,
        projectType
      });

      // Add files to processing queue
      await fileProcessingService.addFilesToQueue(
        uploadedFiles,
        projectType,
        collection._id,
        userInfo
      );

      // Notify parent component to update cache - increment file count for each uploaded file
      if (onCollectionUpdated && collection._id) {
        uploadedFiles.forEach(() => {
          onCollectionUpdated(collection._id, 'fileUploaded');
        });
      }

      // Hide upload area
      setShowUploadArea(false);

      // No need for manual refresh - WebSocket will handle it
      // The file:uploaded event will trigger fetchFiles()

    } catch (error) {
      console.error('File upload error:', error);
    }
  };

  const handleQueueChange = (status) => {
    // This can be removed or kept as a fallback
    // WebSocket events should handle the updates now
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      handleFileUpload(selectedFiles);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Filter and sort files
  const filteredAndSortedFiles = React.useMemo(() => {
    let filtered = files;

    // Apply search filter
    if (searchQuery) {
      filtered = files.filter(file =>
        file.originalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (file.displayKey && file.displayKey.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.originalName.localeCompare(b.originalName);
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'size':
          return (b.fileSize || 0) - (a.fileSize || 0);
        case 'modified':
        default:
          return new Date(b.updatedAt) - new Date(a.updatedAt);
      }
    });

    return sorted;
  }, [files, searchQuery, sortBy]);

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown';

    const now = new Date();
    const past = new Date(date);
    const diffTime = Math.abs(now - past);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      return diffHours <= 1 ? 'Just now' : `${diffHours} hours ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const handleContextMenu = (e, file) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      selectedFile: file
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
      selectedFile: null
    });
  };

  const handleDeleteFile = async () => {
    const fileToDelete = contextMenu.selectedFile;
    if (!fileToDelete || !userInfo?.userId) return;

    const confirmMessage = `Are you sure you want to permanently delete "${fileToDelete.originalName}"? This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingFileId(fileToDelete._id);

      // Call the API to delete the file
      const result = await ProjectService.deleteFile(fileToDelete._id, userInfo.userId);

      if (result.success) {
        // Remove the file from local state immediately (optimistic update)
        setFiles(prevFiles => prevFiles.filter(f => f._id !== fileToDelete._id));

        // Notify parent component about the deletion
        if (onFileDeleted) {
          onFileDeleted(fileToDelete._id, collection?._id);
        }

        // Show success message (you could use a toast library here)
        console.log('File deleted successfully:', result.message);

        // If there were non-critical errors, log them
        if (result.deletionSummary?.errors?.length > 0) {
          console.warn('Deletion completed with warnings:', result.deletionSummary.errors);
        }

        // WebSocket event will confirm the deletion for other clients
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Failed to delete file: ${error.message}`);
    } finally {
      setDeletingFileId(null);
      handleCloseContextMenu();
    }
  };

  const handleRenameFile = () => {
    // TODO: Implement file renaming functionality
    console.log('Rename file:', contextMenu.selectedFile?.originalName);
    handleCloseContextMenu();
  };

  const handleDownloadCSV = async () => {
    if (!collection || !project) {
      console.error('Missing collection or project data');
      return;
    }

    try {
      setIsDownloading(true);

      // Create a collection object with the current files
      const collectionWithFiles = {
        ...collection,
        files: files
      };

      const result = await exportService.downloadCollectionAsCSV(
        collectionWithFiles,
        project._id,
        project.name
      );

      if (!result.success) {
        console.error('Failed to download CSV:', result.error);
        alert('Failed to download CSV. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('An error occurred while downloading the CSV.');
    } finally {
      setIsDownloading(false);
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="h-8 w-8 text-red-400" />;
      case 'jpeg':
      case 'jpg':
      case 'png':
      case 'webp':
      case 'tif':
        return <Image className="h-8 w-8 text-green-400" />;
      default:
        return <FileText className="h-8 w-8 text-blue-400" />;
    }
  };

  // Add visual indicator for processing status
  const getProcessingStatusBadge = (file) => {
    if (!file.processingStatus) return null;

    const statusConfig = {
      pending: { text: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
      processing: { text: 'Processing', className: 'bg-blue-500/20 text-blue-400 border-blue-500/50 animate-pulse' },
      completed: { text: 'Ready', className: 'bg-green-500/20 text-green-400 border-green-500/50' },
      failed: { text: 'Failed', className: 'bg-red-500/20 text-red-400 border-red-500/50' }
    };

    const config = statusConfig[file.processingStatus];
    if (!config) return null;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${config.className}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">Error loading files</div>
        <div className="text-slate-400 text-sm">{error}</div>
        <button
          onClick={() => fetchFiles()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Breadcrumb */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Collections
          </Button>
          <div className="flex items-center gap-2 text-slate-500">
            <span>/</span>
            <span className="text-slate-300">{project?.name}</span>
            <span>/</span>
            <span className="text-slate-300">{collection?.name}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handleDownloadCSV}
            disabled={isDownloading || files.length === 0}
            className="bg-[#494646] hover:bg-[#151415]/50 text-white disabled:opacity-50"
            title={files.length === 0 ? "No files to download" : "Download collection as CSV"}
          >
            <FileDown className="h-4 w-4 mr-2" />
            {isDownloading ? 'Downloading...' : 'Download CSV'}
          </Button>
          <Button
            onClick={handleUploadClick}
            className="bg-[#494646] hover:bg-[#151415]/50 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Upload Files
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* File Upload Area */}
      {showUploadArea && (
        <FileUploadArea
          onFilesSelected={handleFileUpload}
          onCancel={() => setShowUploadArea(false)}
          className="mb-6"
        />
      )}


      {/* Files Display */}
      {filteredAndSortedFiles.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-slate-500 mx-auto mb-4" />
          <div className="text-slate-400 mb-2">
            {searchQuery ? `No files found matching "${searchQuery}"` : "No files found"}
          </div>
          <div className="text-slate-500 text-sm">
            {searchQuery ? "Try adjusting your search terms." : "This collection doesn't have any files yet."}
          </div>
        </div>
      ) : viewMode === "list" ? (
        <div className="space-y-4">
          {filteredAndSortedFiles.map((file) => (
            <Card
              key={file._id}
              className={`group hover:shadow-md cursor-pointer border-[#494646] bg-[#2a282a]/40 hover:bg-[#151415]/30 hover:border-[#494646] ${
                deletingFileId === file._id ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={() => onFileClick && onFileClick(file)}
              onContextMenu={(e) => handleContextMenu(e, file)}
            >
              <CardContent className="p-2 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-12 bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
                      <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                        {getFileIcon(file.fileType)}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-slate-100 text-lg">{file.originalName}</h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-800 text-slate-400 border border-slate-600">
                          {file.fileType?.toUpperCase()}
                        </span>
                        {getProcessingStatusBadge(file)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                        <span>{formatFileSize(file.fileSize)}</span>
                        <span>•</span>
                        <span>Uploaded {formatTimeAgo(file.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-6 text-sm text-slate-400">
                      {file.tableCount !== undefined && file.tableCount !== null && (
                        <div className="flex items-center gap-1">
                          <Table2 className="h-4 w-4" />
                          <span>{file.tableCount} tables</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatTimeAgo(file.updatedAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        <span>{file.uploadedBy || 'System'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredAndSortedFiles.map((file) => (
            <Card
              key={file._id}
              className={`group hover:shadow-lg transition-all duration-200 cursor-pointer border-[#494646] bg-[#2a282a]/40 hover:bg-[#151415]/30 hover:border-[#494646] ${
                deletingFileId === file._id ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={() => onFileClick && onFileClick(file)}
              onContextMenu={(e) => handleContextMenu(e, file)}
            >
              <CardContent className="p-2">
                {/* Compact Header */}
                <div className="h-20 bg-gradient-to-br from-[#494646] to-[#fef3ec]/20 rounded-t-lg relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-green-500/5 to-purple-500/5" />
                  {getFileIcon(file.fileType)}

                  {/* File type and status badges */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-slate-800 text-slate-400 border border-slate-600">
                      {file.fileType?.toUpperCase()}
                    </span>
                  </div>
                  {file.processingStatus && file.processingStatus !== 'completed' && (
                    <div className="absolute bottom-2 left-2">
                      {getProcessingStatusBadge(file)}
                    </div>
                  )}
                </div>

                <div className="p-3 mt-2">
                  {/* Title */}
                  <div className="flex items-start gap-2 mb-2">
                    <h3 className="font-medium text-left line-clamp-1 text-slate-100 text-sm leading-tight">
                      {file.originalName}
                    </h3>
                  </div>

                  {/* Stats - Compact layout */}
                  <div className="grid grid-cols-3 gap-2 text-xs text-slate-400 mb-2">
                    {file.tableCount !== undefined && file.tableCount !== null && (
                      <div className="flex items-center gap-1">
                        <Table2 className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{file.tableCount}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Download className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">0</span>
                    </div>
                    {file.fileSize && (
                      <div className="flex items-center gap-1">
                        <Box className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{formatFileSize(file.fileSize)}</span>
                      </div>
                    )}
                  </div>

                  {/* Last modified */}
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Clock className="h-3 w-3" />
                    <span className="truncate">{formatTimeAgo(file.updatedAt)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add File Card */}
          <Card
            className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-2 bg-[#151415]/30 border-dashed  hover:border-[#fef3ec]/50 hover:bg-[#151415]"
            onClick={handleUploadClick}
          >
            <CardContent className="p-0">
              <div className="h-20 rounded-t-lg flex items-center justify-center">
                <Plus className="h-8 w-8 text-slate-500 group-hover:text-blue-400 transition-colors" />
              </div>
              <div className="p-3 text-center">
                <div className="text-sm text-slate-500 group-hover:text-white transition-colors font-medium mb-1">
                  Upload File
                </div>
                <p className="text-xs text-slate-600">
                  Add files to collection
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Context Menu for file operations */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onClose={handleCloseContextMenu}
        onRename={handleRenameFile}
        onDelete={handleDeleteFile}
        itemName={contextMenu.selectedFile?.originalName || 'file'}
        itemType="file"
      />
    </div>
  );
}